import React from 'react';
import { PlanOptionSelector } from './PlanOptionSelector';
import { CoolnessIndicator } from './CoolnessIndicator';
import { usePlanBuilder } from './usePlanBuilder';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Cell } from 'recharts';
import { Card, CardContent, CardHeader, Typography, Divider, CardActions, Container, Button, Snackbar, Alert, AlertColor } from '@mui/material';
import { PlanOption } from './types';
import { TextField } from '@mui/material';
import { useState } from 'react';
import { PRODUCT_ORDER_MANAGEMENT_API_URL } from '../../utils/constants';

export const PlanBuilder: React.FC = () => {
  const { plan, planOptions, updatePlan, calculateTotalCost, calculateCoolnessFactor } = usePlanBuilder();
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [isPhoneVerified, setIsPhoneVerified] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const totalCost = calculateTotalCost();
  const coolnessFactor = calculateCoolnessFactor();

  const chartData = planOptions.map((option: PlanOption) => ({
    name: option.name.charAt(0).toUpperCase() + option.name.slice(1),
    cost: plan[option.name as keyof typeof plan] * option.pricePerUnit,
    fullName: `${option.name.charAt(0).toUpperCase() + option.name.slice(1)} (${option.unit})`,
  }));

  const colors = ['#8884d8', '#82ca9d', '#ffc658'];

  const PhoneNumberDisplay = () => (
    <div className="mb-6 flex items-center justify-center gap-4">
      <Typography variant="h6" className="text-gray-600">Phone Number:</Typography>
      <Typography variant="h6" className="font-bold">{phoneNumber}</Typography>
      <Button 
        variant="text" 
        color="primary" 
        size="small"
        onClick={() => setIsPhoneVerified(false)}
      >
        Change Number
      </Button>
    </div>
  );

  const handleCreatePlan = async () => {
    setIsProcessing(true);
    try {
      const orderData = {
        description: `Custom Plan for ${phoneNumber}`,
        category: 'Mobile Plan',
        notificationContact: phoneNumber,
        orderTotalPrice: [
          {
            value: totalCost,
            unit: 'USD',
            priceType: 'recurring',
            price: {
              taxIncludedAmount: {
                value: totalCost,
                unit: 'USD'
              }
            }
          }
        ],
        orderItem: planOptions.map((option: PlanOption) => ({
          id: `${option.name}-${Date.now()}`,
          action: 'add',
          quantity: plan[option.name as keyof typeof plan],
          itemPrice: [
            {
              value: option.pricePerUnit,
              unit: 'USD',
              priceType: 'usage'
            }
          ],
          itemTotalPrice: [
            {
              value: plan[option.name as keyof typeof plan] * option.pricePerUnit,
              unit: 'USD',
              priceType: 'recurring'
            }
          ],
          productOffering: {
            id: "627f9533-16e4-449a-8049-66b024bb40e6",
            name: option.name.charAt(0).toUpperCase() + option.name.slice(1)
          }
        }))
      };

      const response = await fetch(`${PRODUCT_ORDER_MANAGEMENT_API_URL}/productOrder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        throw new Error('Failed to create plan');
      }

      setNotification({
        open: true,
        message: 'Plan created successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error creating plan:', error);
      setNotification({
        open: true,
        message: 'Failed to create plan. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  if (!isPhoneVerified) {
    return (
      <Container sx={{ mx: 'auto', pt: 5 }}>
        <Card className="w-full max-w-md bg-white shadow-xl" sx={{ mx: 'auto', mt: 5, p: 3 }}>
          <CardHeader title={
            <Typography variant="h4" className="text-center text-primary">Enter Your Phone Number</Typography>
          } />
          <CardContent>
            {phoneNumber && <PhoneNumberDisplay />}
            <TextField
              fullWidth
              label="Phone Number"
              variant="outlined"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="(123) 456-7890"
            />
          </CardContent>
          <CardActions className="justify-center">
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => setIsPhoneVerified(true)}
              disabled={!phoneNumber}
            >
              Continue
            </Button>
          </CardActions>
        </Card>
      </Container>
    );
  }

  return (
    <Container sx={{ mx: 'auto', pt: 5 }}>
      <Card className="w-full max-w-3xl bg-white shadow-xl" sx={{ mx: 'auto', mt: 5, p: 3 }}>
        <CardHeader title={
          <Typography variant="h3" sx={{ mb: 2}} className="text-center text-primary">Build Your Own Plan</Typography>
        } />
        <CardContent>
          <PhoneNumberDisplay />
          <Divider className="mb-6" />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {planOptions.map((option: PlanOption) => (
              <PlanOptionSelector
                key={option.name}
                option={option}
                value={plan[option.name as keyof typeof plan]}
                onChange={(value) => updatePlan(option.name as keyof typeof plan, value)}
              />
            ))}
          </div>
          
          <Divider className="my-6" />
          
          <CoolnessIndicator coolnessFactor={coolnessFactor} />
          
          <Divider className="my-6" />
          
          <div className="mt-6 pb-3">
            <h3 className="text-lg font-bold mb-4 text-primary pb-3">Cost Breakdown</h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="cost">
                  {chartData.map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                  ))}
                  <LabelList
                    dataKey="cost"
                    position="top"
                    formatter={(value: number) => `$${value.toFixed(2)}`}
                  />
                  <LabelList
                    dataKey="fullName"
                    position="center"
                    fill="#ffffff"
                    fontSize={12}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="w-full text-center">
            <h2 className="text-2xl font-bold text-primary">Total Cost: ${totalCost.toFixed(2)}/month</h2>
          </div>
        </CardContent>
        <CardActions className="justify-center">
          <Button 
            variant="contained" 
            color="primary" 
            sx={{ mt: 2 }}
            onClick={handleCreatePlan}
            disabled={isProcessing}
          >
            {isProcessing ? 'Creating Plan...' : 'Create Plan'}
          </Button>
        </CardActions>
      </Card>
      
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};